import { createSelector } from "@ngrx/store";

import { IAppState } from "@edp-store/state/app.state";
import { IBenefitsState } from "@edp-store/state/benefits.state";
import {
    IFilter,
    Card,
    CardType,
    ExclusiveBenefitsCard,
    IBenefitsUserDetails,
    BenefitsDistricts,
    AvailableOfferFilter,
} from "@edp-models/benefits.model";
import { FilterCard } from "@edp-models/benefitsv3.model";
import { SetAvailableOffers } from "@edp-store/actions/benefits.actions";

const benefitsSelector = (state: IAppState) => state.benefits;
const filtersSelector = (state: IAppState) => state.benefits.filters;
const cardsSelector = (state: IAppState) => state.benefits.cards;
const generatedCodeCardsSelector = (state: IAppState) => state.benefits.generatedCodeCards;
const exclusiveBenefitsSelector = (state: IAppState) => state.benefits.exclusiveBenefitsCards;
const benefitsUsersDetailsSelector = (state: IAppState) => state.benefits.benefitsUsers;
const districtsSelector = (state: IAppState) => state.benefits.districts;
const availableOfferSelector = (state: IAppState) => state.benefits.availableOffers;
const availableOffersFilterSelector = (state: IAppState) => state.benefits.availableOffersFilter;

export const getAvailable = createSelector(benefitsSelector, (state: IBenefitsState) => state);

export const getListBenefitsUsersDetails = createSelector(
    benefitsUsersDetailsSelector,
    (benefitsUsers: IBenefitsUserDetails[]) => benefitsUsers
);

export const benefitsGetFilters = createSelector(getAvailable, (state: { filters: IFilter[] }) => state.filters);

export const filteredCards = createSelector(filtersSelector, cardsSelector, (filters: IFilter[], cards: Card[]) => {
    if (filters && cards) {
        if (filters.length > 0) {
            const stateFilters = filters.filter(flt => !flt.isMobileFilter);
            let selectedFilter: number[] = [];
            stateFilters.forEach(filter => {
                selectedFilter =
                    filter.options.some(opt => opt.selected) || !filter.allSelected ? [...selectedFilter, filter.id] : [...selectedFilter];
            });

            const _areaFiltered = selectedFilter.some(ftrId => ftrId === 1);
            const _typeFiltered = selectedFilter.some(ftrId => ftrId === 2);
            const _statusFiltered = selectedFilter.some(ftrId => ftrId === 3);

            const excludedCardSubtypes = ["INDIVIDUAL_CHALLENGE", "COLLECTIVE_CHALLENGE", "CONTENT"];
            return cards.filter(card => {
                if (excludedCardSubtypes.includes(card.cardSubTypeAcronym)) {
                    return false;
                }
                let cardValid = 0;
                stateFilters.forEach(flt => {
                    if (_areaFiltered && flt.id === 1) {
                        cardValid = flt.options.some(opt => opt.selected && opt.id === card.cardTypeId) ? ++cardValid : cardValid;
                    }
                    if (_typeFiltered && flt.id === 2) {
                        cardValid = flt.options.some(opt => opt.selected && opt.value === card.cardSubTypeAcronym)
                            ? ++cardValid
                            : cardValid;
                    }
                    if (_statusFiltered && flt.id === 3) {
                        cardValid = flt.options.some(opt => opt.selected && opt.value === card.status) ? ++cardValid : cardValid;
                    }
                });
                return cardValid === selectedFilter.length;
            });
        }
    } else {
        return null;
    }
});

export const getMultiCodesPartnerCards = createSelector(cardsSelector, (cards: Card[]) => cards.filter(card => card.type.value === 6));

export const filteredCardsGeneratedCodes = createSelector(
    filtersSelector,
    generatedCodeCardsSelector,
    (filters: IFilter[], generatedCodeCards: Card[]) => {
        if (filters && generatedCodeCards) {
            if (filters.length > 0) {
                // Only Area Filter
                const stateFilters = filters.find(flt => flt.id === 1);
                let selectedFilter: number[] = [];
                selectedFilter = stateFilters.options.some(opt => opt.selected)
                    ? [...selectedFilter, stateFilters.id]
                    : [...selectedFilter];

                if (selectedFilter.length === 0) {
                    return generatedCodeCards;
                }

                const excludedCardSubtypes = ["INDIVIDUAL_CHALLENGE", "COLLECTIVE_CHALLENGE"];
                return generatedCodeCards.filter(card => {
                    if (excludedCardSubtypes.includes(card.cardSubTypeAcronym)) {
                        return false;
                    }
                    return stateFilters.options.some(opt => opt.selected && opt.id === card.cardTypeId);
                });
            }
        } else {
            return null;
        }
    }
);

export const loadedCards = createSelector(
    (state: IAppState) => state.benefits.loadedCards,
    (loadedCards: boolean) => loadedCards
);

export const getCardTypes = createSelector(
    (state: IAppState) => state.benefits.cardTypes,
    (cardTypes: CardType[]) => cardTypes
);

export const reloadFilters = createSelector(benefitsSelector, (state: IBenefitsState) => state.reloadFilters);

export const getErrorBenefits = createSelector(benefitsSelector, (state: IBenefitsState) => state.errorBenefits);

export const highlightedCards = createSelector(cardsSelector, (cards: Card[]) =>
    cards.filter(
        card =>
            (card.highlight && card.status === "AVAILABLE") ||
            (card.highlight && card.status === "FINESHED" && card.cardSubTypeAcronym === "DRAW")
    )
);

export const generatedCodesCards = createSelector(generatedCodeCardsSelector, (cards: Card[]) => cards);

export const getExclusiveBenefitsCards = createSelector(
    exclusiveBenefitsSelector,
    (exclusiveCards: ExclusiveBenefitsCard[]) => exclusiveCards
);

export const getAvailableForYou = createSelector(cardsSelector, (cards: Card[]) => {
    const packEdp = cards.filter(card => card.cardTypeId === 1);
    return packEdp;
});

export const getCounterGeneratedCodes = createSelector(benefitsSelector, (state: IBenefitsState) => state.counterPartnerBenefits);

export const getShareModalStatus = createSelector(benefitsSelector, (state: IBenefitsState) => state.shareModal);

export const getCodeValidaty = createSelector(benefitsSelector, (state: IBenefitsState) => state.codeValidity);

export const getTransactions = createSelector(benefitsSelector, (state: IBenefitsState) => state.transactions);

export const getBenefitBalance = createSelector(benefitsSelector, (state: IBenefitsState) => state.balance);

export const getUserPartnerNumber = createSelector(benefitsSelector, (state: IBenefitsState) => state.benefitUserSelected?.pn);

export const getUserBenefit = createSelector(benefitsSelector, (state: IBenefitsState) => state.benefitUserSelected);

export const getAvailableOfferFilter = createSelector(cardsSelector, availableOffersFilterSelector, (cards: Card[], filters) => {
    const countByCardSubType = new Map<string, number>();
    const cardDetailsMap = new Map<string, { title: string; icon: string; acronym: string }>();
    const customOrder = {
        ALL: 0,
        DAY_TO_DAY: 1,
        SPORT_OUTDOORS: 2,
        FAMILY: 3,
        WELLBEING_HEALTH: 4,
        RESTAURANTS: 5,
        ACCOMMODATION: 6,
        PRODUCTS_SERVICES: 7,
        DRAW: 8,
        OTHERS: 9,
    };

    const districtAcronym = filters?.districtAcronym;

    const newCards = cards.filter(offer => {
        const districtMatches =
            !districtAcronym ||
            offer.partnerTypeAcronym === "EDPC" ||
            districtAcronym === "TODOS" ||
            (offer.districts &&
                offer.districts.some(district => district && (district.acronym === districtAcronym || district.acronym === "TODOS")));
        return districtMatches;
    });

    newCards.forEach(({ cardSubTypeAcronym, cardTitle, icon, filterSubtype }) => {
        if (filterSubtype.includes(cardSubTypeAcronym)) {
            countByCardSubType.set(cardSubTypeAcronym, (countByCardSubType.get(cardSubTypeAcronym) ?? 0) + 1);
            cardDetailsMap.set(cardSubTypeAcronym, { title: cardTitle, icon, acronym: cardSubTypeAcronym });
        } else {
            countByCardSubType.set("OTHERS", (countByCardSubType.get("OTHERS") ?? 0) + 1);
            cardDetailsMap.set("OTHERS", {
                title: "pt.edp.edponline.benefits.filters.cardSubtype.OTHERS",
                icon: "icon-edponline-Bop-226",
                acronym: "OTHERS",
            });
        }
    });

    const totalCount = newCards.length;
    const allObject = { name: "pt.edp.edponline.benefits.filters.cardSubtype.ALLPACKSUBTYPES", quantity: totalCount, acronym: "ALL" };

    const filterCards: FilterCard[] = [
        allObject,
        ...Array.from(countByCardSubType.entries())
            .filter(([_, quantity]) => quantity > 0)
            .sort(([a], [b]) => customOrder[a] - customOrder[b])
            .map(([name, quantity]) => {
                const { title, icon, acronym } = cardDetailsMap.get(name);
                return { name: title, icon, quantity, acronym };
            }),
    ];

    return filterCards;
});

export const getDistricts = createSelector(districtsSelector, (districts: BenefitsDistricts[]) =>
    districts.filter(filter => filter.acronym !== "TODOS")
);

export const getAvailableOffers = (acronym?: string) =>
    createSelector(cardsSelector, (cards: Card[]) => {
        if (!acronym) {
            return cards;
        } else {
            return cards.filter(offer => {
                if (acronym === "OTHERS") {
                    return !offer.filterSubtype.includes(offer.cardSubTypeAcronym);
                } else if (acronym === "TODOS") {
                    return true;
                } else {
                    return offer.cardSubTypeAcronym === acronym;
                }
            });
        }
    });

// export const getAvailableOffersByDistrict = (acronym?: string) =>
//     createSelector(cardsSelector, (cards: Card[]) => {
//         if (!acronym) {
//             return cards;
//         } else if (acronym === "TODOS") {
//             return cards;
//         } else {
//             return cards.filter(
//                 card => card.districts && card.districts.some(district => district && district.acronym === acronym)
//             );
//         }
//     });

export const getAvailableOffersFilters = createSelector(availableOffersFilterSelector, (filters: AvailableOfferFilter) => filters);

export const getFilteredOffers = createSelector(
    availableOffersFilterSelector,
    cardsSelector,
    (filters: AvailableOfferFilter, cards: Card[]) => {
        if (!filters) {
            return cards;
        } else {
            const { offerAcronym, districtAcronym } = filters;
            const filtered = cards.filter(offer => {
                const offerMatches =
                    !offerAcronym ||
                    offerAcronym === "ALL" ||
                    (offerAcronym === "OTHERS" && !offer.filterSubtype.includes(offer.cardSubTypeAcronym)) ||
                    offer.cardSubTypeAcronym === offerAcronym;

                const districtMatches =
                    !districtAcronym ||
                    offer.partnerTypeAcronym === "EDPC" ||
                    districtAcronym === "TODOS" ||
                    (offer.districts &&
                        offer.districts.some(
                            district => district && (district.acronym === districtAcronym || district.acronym === "TODOS")
                        ));
                return offerMatches && districtMatches;
            });

            return filtered;
        }
    }
);
